/**
 * Email gateway contracts (T-013) — docs/10-design/07 §3 · tasks/W1-CONTRACTS §7
 * ใช้ร่วมระหว่าง worker (`/api/v1/email/*`) กับระบบภายนอก/SDK
 */
import { z } from "zod";

export const EMAIL_STREAMS = ["otp", "security", "ticket", "notification", "billing", "kyc", "bulk"] as const;
export type EmailStreamId = (typeof EMAIL_STREAMS)[number];

/** Header inputs are single-line values; folding is exclusively serializer-owned. */
const headerValue = z.string().regex(/^[^\r\n\0]*$/, "Header controls are not allowed");
export const EmailContentType = headerValue.regex(/^[!#$%&'*+.^_`|~0-9A-Za-z-]+\/[!#$%&'*+.^_`|~0-9A-Za-z-]+(?:[ \t]*;[ \t]*[!#$%&'*+.^_`|~0-9A-Za-z-]+=(?:[!#$%&'*+.^_`|~0-9A-Za-z-]+|"(?:[^"\\\r\n\0]|\\[\x20-\x7e])*"))*[ \t]*$/, "Invalid MIME content type");
/** Canonical padded RFC 4648 base64, including an explicit zero-byte file. */
export function isCanonicalAttachmentBase64(value: string): boolean {
  if (value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) return false;
  // Check unused padding bits without allocating decoded file bytes in shared contracts.
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  if (value.endsWith("==")) return (alphabet.indexOf(value.at(-3)!) & 15) === 0;
  if (value.endsWith("=")) return (alphabet.indexOf(value.at(-2)!) & 3) === 0;
  return true;
}
export const EmailAttachmentContent = z.string().max(2_800_000).refine(isCanonicalAttachmentBase64, "Expected canonical base64");

export const EmailAddress = z.object({
  email: z.email().max(254),
  name: headerValue.max(120).optional(),
});
export type EmailAddress = z.infer<typeof EmailAddress>;

/** ไฟล์แนบ inline base64 (≤2 MB ต่อไฟล์ — ตรวจขนาดจริงที่ handler เพื่าตอบ 413) · attachment_id = Wave ถัดไป */
export const EmailAttachmentInput = z.object({
  filename: headerValue.min(1).max(255),
  content_type: EmailContentType.max(127).optional(),
  content: EmailAttachmentContent, // base64 ของ ≤2 MB
  attachment_id: z.string().max(64).optional(),
});

export const EmailRecipientPolicyRef=z.object({subject:z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),contact:z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),category:z.string().regex(/^[A-Za-z0-9._:-]{1,64}$/),policy_version:z.number().int().min(1).max(2147483647)}).strict();

export const EmailSendRequest = z
  .object({
    recipient_policy: EmailRecipientPolicyRef.optional(),
    stream: headerValue.min(1).max(64),
    from: EmailAddress,
    to: z.array(EmailAddress).min(1).max(50),
    cc: z.array(EmailAddress).max(50).default([]),
    bcc: z.array(EmailAddress).max(50).default([]),
    reply_to: EmailAddress.nullish(),
    subject: headerValue.min(1).max(998),
    html: z.string().max(10_000_000).optional(),
    text: z.string().max(10_000_000).optional(),
    attachments: z.array(EmailAttachmentInput).max(10).optional(),
    headers: z.record(z.string().min(1).max(128).regex(/^[!-9;-~]+$/), headerValue.max(998)).optional(),
    tags: z.array(z.string().min(1).max(64)).max(10).optional(),
    metadata: z.record(z.string().max(128), z.unknown()).optional(),
    thread: z.object({ ticket_id: z.string().min(6).max(32), in_reply_to: headerValue.max(998).optional() }).optional(),
    unsubscribe: z.object({ group: z.string().min(1).max(128) }).optional(),
    send_at: z.iso.datetime().optional(),
    /** Correlation only: UWU ID subject, never an actor or authorization grant. */
    related_user_sso_sub: z.uuid().optional(),
    /** Caller business deadline; no default is inferred for OTP or legacy callers. */
    expires_at: z.iso.datetime().optional(),
    /** [T-320 · EG-ID #315] business-intent reference เสถียรของ client — replay/lookup ข้ามการหมุน API key */
    client_reference: z.string().regex(/^[A-Za-z0-9._:-]{1,256}$/).optional(),
    priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
    options: z
      .object({
        provider: z.enum(["auto", "local", "sandbox"]).optional(),
        partial: z.boolean().optional(),
      })
      .optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (!v.html && !v.text) {
      ctx.addIssue({ code: "custom", path: ["html"], message: "ต้องมี html หรือ text อย่างน้อยหนึ่งอย่าง" });
    }
    const total = v.to.length + v.cc.length + v.bcc.length;
    if(v.recipient_policy && total!==1) ctx.addIssue({code:"custom",path:["recipient_policy"],message:"recipient policy รองรับหนึ่งผู้รับต่อข้อความ"});
    if (total > 50) {
      ctx.addIssue({ code: "custom", path: ["to"], message: `ผู้รับรวม to+cc+bcc ต้องไม่เกิน 50 (ได้ ${total})` });
    }
    if (v.stream === "bulk" && !v.unsubscribe) {
      ctx.addIssue({ code: "custom", path: ["unsubscribe"], message: "stream bulk ต้องมี unsubscribe" });
    }
    if (!v.html && v.text === "" && v.attachments === undefined) {
      // text เปล่า ๆ อย่างเดียวไม่นับว่ามีเนื้อหา
      if (!v.html) ctx.addIssue({ code: "custom", path: ["text"], message: "text ต้องไม่ว่าง" });
    }
    const seen = new Set<string>();
    for (const [kind, list] of [["to", v.to], ["cc", v.cc], ["bcc", v.bcc]] as const) {
      for (const r of list) {
        const k = `${r.email.toLowerCase()}|${kind}`;
        if (seen.has(k)) ctx.addIssue({ code: "custom", path: [kind], message: `ที่อยู่ซ้ำ: ${r.email}` });
        seen.add(k);
      }
    }
    if (v.attachments) {
      for (const [i, a] of v.attachments.entries()) {
        if (a.attachment_id !== undefined && a.content === undefined) {
          ctx.addIssue({ code: "custom", path: [`attachments.${i}`], message: "attachment_id ยังไม่รองรับใน Wave นี้ (ใช้ content base64)" });
        }
        if (a.attachment_id !== undefined && a.content !== undefined) {
          ctx.addIssue({ code: "custom", path: [`attachments.${i}`], message: "ระบุ content หรือ attachment_id อย่างใดอย่างหนึ่ง" });
        }
      }
    }
  });
export type EmailSendRequest = z.infer<typeof EmailSendRequest>;

/** Subject delivery excludes address-bearing and multi-recipient inputs. Construct
 * from the original field schemas: omitting on a refined Zod object would retain
 * refinements which assume to/cc/bcc still exist. */
const {
  to: _to, cc: _cc, bcc: _bcc, thread: _thread,
  related_user_sso_sub: _related, headers: _headers, unsubscribe: _unsubscribe,
  options: _options, ...subjectSendFields
} = EmailSendRequest.shape;
export const EMAIL_SUBJECT_STREAMS = ["otp", "security", "ticket", "notification", "billing", "kyc"] as const;
export const EmailSubjectSendRequest = z.object({
  ...subjectSendFields,
  stream: z.enum(EMAIL_SUBJECT_STREAMS),
  recipient: z.object({ sub: z.uuid() }).strict(),
  expires_at: z.iso.datetime(),
  options: z.object({ provider: z.enum(["auto", "local", "sandbox"]).optional() }).strict().optional(),
}).strict().superRefine((v, ctx) => {
  // Reuse every direct content/header/attachment refinement with one synthetic
  // recipient, discarding only the synthetic address from the parsed result.
  const { recipient, ...content } = v;
  const parsed = EmailSendRequest.safeParse({ ...content, to: [{ email: "subject-validation@example.invalid" }] });
  if (!parsed.success) for (const issue of parsed.error.issues) ctx.addIssue({ code: "custom", path: issue.path, message: issue.message });
  if (v.recipient_policy && (v.recipient_policy.subject !== recipient.sub || v.recipient_policy.contact !== "uwu-id-primary")) {
    ctx.addIssue({ code: "custom", path: ["recipient_policy"], message: "Subject policy must match recipient.sub and contact uwu-id-primary" });
  }
});
export type EmailSubjectSendRequest = z.infer<typeof EmailSubjectSendRequest>;
export const EmailSubjectRecipient = z.object({ kind: z.literal("uwu_id_subject"), sub: z.uuid() }).strict();
export const EmailSubjectResolutionStatus = z.enum(["pending", "resolved", "unavailable"]);
export const EmailSubjectSendResponse = z.object({
  id: z.string(), status: z.string(), stream: z.string(),
  recipient: EmailSubjectRecipient, resolution_status: EmailSubjectResolutionStatus,
  scheduled_at: z.string().nullable(), expires_at: z.string(), idempotent_replay: z.boolean(),
}).strict();
export type EmailSubjectSendResponse = z.infer<typeof EmailSubjectSendResponse>;
export const EmailSubjectMessage = EmailSubjectSendResponse.omit({idempotent_replay:true}).extend({
  /** null only after approved privacy erasure; acceptance always contains the submitted UUID. */
  recipient: EmailSubjectRecipient.extend({sub:z.uuid().nullable()}).strict(),
  client_reference: z.string().nullable(), send_phase: z.string().nullable(),
  accepted_at: z.string().nullable(), recovery_required: z.boolean(), reason_code: z.string().nullable(),
}).strict();
export type EmailSubjectMessage = z.infer<typeof EmailSubjectMessage>;

export const EmailRecipientOut = z.object({
  email: z.string(),
  kind: z.enum(["to", "cc", "bcc"]),
  status: z.string(),
});
export type EmailRecipientOut = z.infer<typeof EmailRecipientOut>;

export const EmailSendResponse = z.object({
  id: z.string(),
  status: z.string(),
  stream: z.string(),
  recipients: z.array(EmailRecipientOut),
  scheduled_at: z.string().nullable(),
  related_user_sso_sub: z.uuid().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  /** [T-320 · EG-ID #315] null เมื่อผู้ส่งไม่ได้ระบุ (อ่านจากแถวของ message) */
  client_reference: z.string().nullable().optional(),
  idempotent_replay: z.boolean(),
});
export type EmailSendResponse = z.infer<typeof EmailSendResponse>;

export const EmailEvent = z.object({
  id: z.string(),
  message_id: z.string(),
  recipient: z.string().nullable(),
  type: z.string(),
  provider: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  occurred_at: z.string(),
});
export type EmailEvent = z.infer<typeof EmailEvent>;

export const EmailMessage = z.object({
  id: z.string(),
  status: z.string(),
  stream: z.string(),
  priority: z.string(),
  subject: z.string(),
  from: z.object({ email: z.string(), name: z.string().nullable() }),
  reply_to: z.string().nullable(),
  thread: z.object({ ticket_id: z.string().nullable(), ref: z.string().nullable(), in_reply_to: z.string().nullable() }),
  to: z.array(EmailRecipientOut),
  cc: z.array(EmailRecipientOut),
  bcc: z.array(EmailRecipientOut),
  tags: z.array(z.string()),
  metadata: z.record(z.string(), z.unknown()),
  provider: z.string().nullable(),
  provider_message_id: z.string().nullable(),
  attachment_count: z.number().int(),
  size_bytes: z.number().int().nullable(),
  scheduled_at: z.string().nullable(),
  related_user_sso_sub: z.uuid().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  /** [T-320 · EG-ID #315] business-intent reference ของ client ที่ส่งมาตอน submit — null ถ้าไม่ระบุ */
  client_reference: z.string().nullable().optional(),
  /** [T-322 · EG-ID #315] Idempotency-Key ที่ผูกกับแถวนี้ — null ถ้าไม่ได้ส่ง/ถูก detach ตามหน้าต่าง 7 วัน */
  idempotency_key: z.string().nullable().optional(),
  queued_at: z.string().nullable(),
  sent_at: z.string().nullable(),
  created_at: z.string(),
  // [SA-04] read/replay — optional เพิ่มเท่านั้น (contract §Schema reservation): ไม่มี body/token/credential
  send_phase: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  recovery_required: z.boolean().optional(),
});
export type EmailMessage = z.infer<typeof EmailMessage>;

export const EmailMessageList = z.object({
  items: z.array(EmailMessage),
  next_cursor: z.string().nullable(),
});
export type EmailMessageList = z.infer<typeof EmailMessageList>;

export const EmailEventList = z.object({ items: z.array(EmailEvent) });

export const EmailStreamOut = z.object({
  id: z.string(),
  lane: z.string(),
  default_priority: z.string(),
  requires_unsubscribe: z.boolean(),
  body_retention_days: z.number().int().nullable(),
});
export type EmailStreamOut = z.infer<typeof EmailStreamOut>;

export const EmailProvidersHealth = z.object({
  providers: z.array(z.object({ name: z.string(), enabled: z.boolean(), breaker: z.string() })),
});
export type EmailProvidersHealth = z.infer<typeof EmailProvidersHealth>;

export const SuppressionCreate = z.object({
  email: z.email().max(254),
  scope: z.string().min(1).max(128).optional(),
  reason: z.literal("manual").default("manual"),
});
export type SuppressionCreate = z.infer<typeof SuppressionCreate>;

export const SuppressionOut = z.object({
  email_hash: z.string(),
  scope: z.string(),
  email: z.string().nullable(),
  reason: z.string(),
  source: z.string().nullable(),
  message_id: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string().nullable(),
});
export type SuppressionOut = z.infer<typeof SuppressionOut>;

export const SuppressionList = z.object({ items: z.array(SuppressionOut), next_cursor: z.string().nullable() });

export const SuppressionCheckResponse = z.object({
  email_hash: z.string(),
  suppressed: z.boolean(),
  scopes: z.array(z.string()),
});
export type SuppressionCheckResponse = z.infer<typeof SuppressionCheckResponse>;

export const EmailMessageListQuery = z.object({
  related_user_sso_sub: z.uuid().optional(),
  status: z.string().max(64).optional(),
  stream: z.string().max(64).optional(),
  tag: z.string().max(64).optional(),
  to: z.email().optional(),
  since: z.iso.datetime().optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type EmailMessageListQuery = z.infer<typeof EmailMessageListQuery>;

// ---------------------------------------------------------------------------
// [T-321 · EG-ID #315] client-wide event feed — GET /api/v1/email/events
// durable cursor (opaque) + replay · keyset (occurred_at, event_id) asc · filter client ของ principal เสมอ
// item **ห้ามมี** subject/body/html/OTP/ลิงก์ — เฉพาะ metadata การส่ง (มีเทสต์กัน field รั่ว)
// ---------------------------------------------------------------------------

/** event เดียวใน feed — ไม่มีเนื้อหาอีเมล/ผู้รับแบบเต็ม: recipient เป็นที่อยู่ที่ provider รายงาน */
export const EmailFeedEvent = z.object({
  event_id: z.string(),
  message_id: z.string(),
  client_reference: z.string().nullable(),
  stream: z.string(),
  occurred_at: z.string(),
  recipient: z.string().nullable(),
  type: z.string(),
  outcome: z.string().nullable(),
  reason_code: z.string().nullable(),
  retryable: z.boolean().nullable(),
});
export type EmailFeedEvent = z.infer<typeof EmailFeedEvent>;

export const EmailFeedEventList = z.object({
  items: z.array(EmailFeedEvent),
  /** opaque — client ห้ามตีความ · null = จบ feed ณ ตอนนั้น */
  next_cursor: z.string().nullable(),
  /** iso เมื่อ next_cursor จะใช้ไม่ได้ (ตามหน้าต่าง 30 วัน) · null เมื่อ next_cursor = null */
  cursor_expires_at: z.string().nullable(),
});
export type EmailFeedEventList = z.infer<typeof EmailFeedEventList>;

export const EmailFeedQuery = z.object({
  /** opaque cursor จาก response ก่อนหน้า · ผิดรูปแบบ = 422 · หมดอายุ = 410 cursor_expired */
  cursor: z.string().max(1024).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  stream: z.string().max(64).optional(),
  message_id: z.string().min(1).max(64).optional(),
  /** เก่ากว่าหน้าต่าง feed = 410 cursor_expired (เริ่มใหม่จากหัว) */
  since: z.iso.datetime().optional(),
});
export type EmailFeedQuery = z.infer<typeof EmailFeedQuery>;

// ---------------------------------------------------------------------------
// [SA-04 · #265 #266 #282] Admin outbox recovery — contract §Manual unknown resolution
// GET /api/v1/admin/outbox/recovery · GET /api/v1/admin/outbox/messages/:id/recovery
// POST /api/v1/admin/outbox/messages/:id/resolve (admin + CSRF + recent-auth)
// ไม่มี generic retry-unknown endpoint — decision ต้องมีหลักฐาน
// ---------------------------------------------------------------------------

export const OutboxSendPhases = z.enum(["preparing", "dispatching", "unknown", "accepted", "finalized", "not_accepted", "abandoned"]);
export type OutboxSendPhases = z.infer<typeof OutboxSendPhases>;

export const OutboxRecoveryCounts = z.object({
  /** แต่ละหมวดอาจซ้อนกัน (ข้อความเดียวอยู่ได้หลายหมวด) — ห้ามรวมกันเป็น total เดียว */
  queued_due: z.number().int().nonnegative(),
  expired_preparation: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
  accepted_unfinalized: z.number().int().nonnegative(),
  bridge_pending: z.number().int().nonnegative(),
  inbox_unmatched: z.number().int().nonnegative(),
});
export type OutboxRecoveryCounts = z.infer<typeof OutboxRecoveryCounts>;

export const OutboxRecoveryOverview = z.object({
  counts: OutboxRecoveryCounts,
  /** อายุเก่าสุด (วินาที) ของแต่ละหมวด — null = ไม่มีแถว */
  oldest_seconds: z.object({
    queued_due: z.number().nonnegative().nullable(),
    expired_preparation: z.number().nonnegative().nullable(),
    unknown: z.number().nonnegative().nullable(),
    accepted_unfinalized: z.number().nonnegative().nullable(),
    bridge_pending: z.number().nonnegative().nullable(),
    inbox_unmatched: z.number().nonnegative().nullable(),
  }),
  note: z.literal("categories may overlap and are not a summable unique total"),
});
export type OutboxRecoveryOverview = z.infer<typeof OutboxRecoveryOverview>;

export const OutboxRecoveryAttempt = z.object({
  id: z.string().uuid(),
  provider: z.string(),
  phase: OutboxSendPhases,
  lease_until: z.string().nullable(),
  dispatch_started_at: z.string().nullable(),
  dispatch_deadline_at: z.string().nullable(),
  provider_message_id: z.string().nullable(),
  accepted_at: z.string().nullable(),
  last_error: z.record(z.string(), z.unknown()).nullable(),
  resolution: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OutboxRecoveryAttempt = z.infer<typeof OutboxRecoveryAttempt>;

export const OutboxRecoveryAction = z.object({
  action_id: z.string().uuid(),
  attempt_id: z.string().uuid(),
  decision: z.enum(["accepted", "not_accepted", "hold"]),
  reason: z.string(),
  evidence_ref: z.string(),
  outcome: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
});
export type OutboxRecoveryAction = z.infer<typeof OutboxRecoveryAction>;

export const OutboxMessageRecovery = z.object({
  message_id: z.string(),
  status: z.string(),
  provider: z.string().nullable(),
  provider_message_id: z.string().nullable(),
  dispatch_count: z.number().int(),
  prepare_failure_count: z.number().int(),
  current_attempt_id: z.string().uuid().nullable(),
  /** true เมื่อมี attempt ค้าง phase dispatching/unknown ที่รอผล — ต้อง resolve ด้วยหลักฐาน ห้าม resend บนสุ่ม */
  recovery_required: z.boolean(),
  attempts: z.array(OutboxRecoveryAttempt),
  actions: z.array(OutboxRecoveryAction),
  recent_events: z.array(EmailEvent),
});
export type OutboxMessageRecovery = z.infer<typeof OutboxMessageRecovery>;

export const OutboxResolveRequest = z
  .object({
    action_id: z.string().uuid(),
    expected_attempt_id: z.string().uuid(),
    decision: z.enum(["accepted", "not_accepted", "hold"]),
    reason: z.string().min(1).max(2000),
    /** หลักฐานอ้างอิง (เช่น SNS MessageId, provider console URL, incident id) — ต้องมีเสมอ */
    evidence_ref: z.string().min(1).max(512),
    /** จำเป็นเมื่อ decision=accepted */
    provider_message_id: z.string().min(1).max(998).optional(),
    /** จำเป็นเมื่อ decision=not_accepted — คำยืนยันว่า execution เก่าสงบแล้ว (deadline ผ่าน + ตรวจ provider แล้ว) */
    quiescence: z
      .object({
        confirmed: z.literal(true),
        detail: z.string().min(1).max(512),
      })
      .optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.decision === "accepted" && v.provider_message_id === undefined) {
      ctx.addIssue({ code: "custom", path: ["provider_message_id"], message: "decision=accepted ต้องแนบ provider_message_id" });
    }
    if (v.decision === "not_accepted" && v.quiescence === undefined) {
      ctx.addIssue({ code: "custom", path: ["quiescence"], message: "decision=not_accepted ต้องยืนยัน quiescence ของ execution เก่า" });
    }
  });
export type OutboxResolveRequest = z.infer<typeof OutboxResolveRequest>;

export const OutboxResolveResponse = z.object({
  action_id: z.string().uuid(),
  decision: z.enum(["accepted", "not_accepted", "hold"]),
  /** ผลที่เกิดจริง เช่น {message_status:"sent"} — replay คืน outcome ของ action ต้นฉบับ */
  outcome: z.record(z.string(), z.unknown()).nullable(),
  message_id: z.string(),
  message_status: z.string(),
  replayed: z.boolean(),
});
export type OutboxResolveResponse = z.infer<typeof OutboxResolveResponse>;

/** [S354] Commit-safe subscription surface; legacy /events remains pagination-only. */
export const EmailFeedV2Query = z.object({
  cursor:z.string().max(2048).optional(),checkpoint:z.string().max(2048).optional(),
  limit:z.coerce.number().int().min(1).max(100).default(50),
  stream:z.string().min(1).max(64).optional(),message_id:z.string().min(1).max(64).optional(),
}).strict().refine(v=>!(v.cursor&&v.checkpoint),"Use cursor or checkpoint, not both");
export const EmailFeedV2Event=EmailFeedEvent.extend({recipient_subject:z.uuid().optional(),received_at:z.string(),published_at:z.string()});
export const EmailFeedV2EventList=z.object({version:z.literal(2),items:z.array(EmailFeedV2Event),next_cursor:z.string().nullable(),checkpoint:z.string(),checkpoint_expires_at:z.string(),ingestion_pending:z.boolean()});

export const EmailRecipientPolicyQuery=EmailRecipientPolicyRef.omit({policy_version:true});
export const EmailRecipientPolicyMutation=EmailRecipientPolicyRef.extend({action_id:z.uuid(),expected_revision:z.number().int().min(0).max(2147483646),blocked:z.boolean(),valid_until:z.iso.datetime(),reason:z.string().min(1).max(512)}).strict();
export const EmailRecipientPolicyAck=z.object({subject:z.string(),contact:z.string(),category:z.string(),policy_version:z.number().int(),revision:z.number().int(),blocked:z.boolean(),valid_until:z.string(),effective_at:z.string(),replayed:z.boolean(),boundary:z.literal("before_dispatch_marker_only")});

export const EmailPrivacyManifest=z.object({operation_id:z.uuid(),reason:z.string().min(1).max(512),message_ids:z.array(z.string().min(1).max(80)).min(1).max(100)}).strict().refine(v=>new Set(v.message_ids).size===v.message_ids.length,"Duplicate message IDs");
export const EmailPrivacyReview=z.object({action_id:z.uuid(),expected_revision:z.number().int().min(0).max(2147483646),decision:z.enum(["approve","retain","hold"]),reason:z.string().min(1).max(512),evidence_ref:z.string().min(1).max(512)}).strict();
export const EmailPrivacyAck=z.object({operation_id:z.uuid(),phase:z.enum(["awaiting_review","held","retained","objects_pending","completed"]),revision:z.number().int(),completed:z.boolean(),pending_reason:z.string().nullable(),review_decision:z.string().nullable(),objects_total:z.number().int(),objects_acknowledged:z.number().int(),retained_evidence:z.array(z.string())});

export const EmailFeedV2Response=EmailFeedV2EventList;
export const EmailGatewayCapabilities=z.object({version:z.literal(2),durable_feed:z.literal("/api/v1/email/event-feed"),checkpoint_window_days:z.literal(30),max_page_size:z.literal(100),webhook_source_event_id:z.literal(true),recipient_policy:z.literal("exact_version_and_freshness"),privacy:z.literal("reviewed_manifest"),restore:z.literal("external_epoch_signed_replay"),legacy_events:z.literal("pagination_only")});

export const EmailRecipientPolicyStatus=EmailRecipientPolicyAck.omit({replayed:true}).extend({fresh:z.boolean()});

/** Public subject feed always redacts resolved addresses and carries its own checkpoint. */
export const EmailSubjectFeedV2Event=EmailFeedV2Event.extend({recipient:z.null(),recipient_subject:z.uuid().nullable()}).strict();
export type EmailSubjectFeedV2Event=z.infer<typeof EmailSubjectFeedV2Event>;
export const EmailSubjectFeedV2Response=EmailFeedV2Response.extend({items:z.array(EmailSubjectFeedV2Event)}).strict();
export type EmailSubjectFeedV2Response=z.infer<typeof EmailSubjectFeedV2Response>;

export const EmailSubjectClientConfig=z.object({
  version:z.string().min(1).max(64),issuer:z.url(),app_client_id:z.string().min(1).max(128),
  allowed_streams:z.array(z.enum(EMAIL_SUBJECT_STREAMS)).min(1).max(6),
  max_lifetime_seconds:z.number().int().min(60).max(2592000),
  rate_limit_per_subject_per_minute:z.number().int().min(1).max(1000),
}).strict();
export type EmailSubjectClientConfig=z.infer<typeof EmailSubjectClientConfig>;

export const EmailSubjectCancelResponse=z.object({canceled:z.literal(true)}).strict();
export type EmailSubjectCancelResponse=z.infer<typeof EmailSubjectCancelResponse>;
export const EmailSubjectCapabilities=z.object({
  version:z.literal(1),send_endpoint:z.literal("/api/v1/email/subject-messages"),
  receipt_endpoint:z.literal("/api/v1/email/subject-messages/{id}"),
  resolution:z.literal("current_primary_before_dispatch"),recipient_privacy:z.literal("subject_only"),
}).strict();
export type EmailSubjectCapabilities=z.infer<typeof EmailSubjectCapabilities>;
